import { supabase } from './supabase';
import { Order, Product } from '../types';

export interface OrderStats {
  totalOrders: number;
  totalAmount: number;
  averageAmount: number;
  conversionRate: number;
  pendingOrders: number;
  paidOrders: number;
  cancelledOrders: number;
  pendingAmount: number;
  byClass: {
    [className: string]: {
      totalOrders: number;
      totalAmount: number;
      averageAmount: number;
      pendingOrders?: number;
      cancelledOrders?: number;
    };
  };
  evolution: {
    date: string;
    orders: number;
    amount: number;
    averageAmount: number;
    pendingOrders?: number;
    cancelledOrders?: number;
  }[];
  hourlyEvolution: {
    [date: string]: {
      hour: string;
      orders: number;
      amount: number;
      averageAmount: number;
    }[];
  };
}

export interface ProductStats {
  totalProducts: number;
  availableProducts: number;
  outOfStockProducts: number;
  topProducts: Array<{
    id: string;
    name: string;
    totalSold: number;
    totalRevenue: number;
    paidSold: number;
    pendingSold: number;
  }>;
  articleCounts: Array<{
    id: string;
    name: string;
    paidCount: number;
    pendingCount: number;
    stock: number;
    stockPercentage: number;
    availablePacks?: number;
  }>;
  sizeCounts: Array<{
    size: string;
    paidCount: number;
    pendingCount: number;
  }>;
}

class StatsService {
  private static instance: StatsService;

  private constructor() {}

  public static getInstance(): StatsService {
    if (!StatsService.instance) {
      StatsService.instance = new StatsService();
    }
    return StatsService.instance;
  }

  async getOrderStats(): Promise<OrderStats> {
    const { data: orders, error } = await supabase
      .from('orders')
      .select('*')
      .order('date', { ascending: true });

    if (error) {
      throw new Error('Erreur lors de la récupération des commandes');
    }

    const totalOrders = orders.length;
    const totalAmount = orders.reduce((sum, order: Order) => {
      if (order.status === 'paid') {
        return sum + order.total;
      }
      return sum
    }, 0);
    const averageAmount = totalOrders > 0 ? totalAmount / totalOrders : 0;
    const pendingOrders = orders.filter((order: Order) => order.status === 'pending').length;
    const paidOrders = orders.filter((order: Order) => order.status === 'paid').length;
    const cancelledOrders = orders.filter((order: Order) => order.status === 'cancelled').length;
    const conversionRate = totalOrders > 0 ? (paidOrders / totalOrders) * 100 : 0;
    const pendingAmount = orders.reduce((sum, order: Order) => {
      if (order.status === 'pending') {
        return sum + order.total;
      }
      return sum;
    }, 0);

    // Statistiques par classe
    const byClass: OrderStats['byClass'] = {};
    orders.forEach((order: Order) => {
      if (order.status === 'paid') {
      const className = order.customer_info.class || 'Autre';
      if (!byClass[className]) {
        byClass[className] = {
          totalOrders: 0,
          totalAmount: 0,
          averageAmount: 0,
          pendingOrders: 0,
          cancelledOrders: 0
        };
      }
      byClass[className].totalOrders++;
      byClass[className].totalAmount += order.total;
  }});

    // Calcul des moyennes par classe
    Object.keys(byClass).forEach(className => {
      byClass[className].averageAmount = byClass[className].totalAmount / byClass[className].totalOrders;
    });

    // Évolution des ventes par jour
    const evolutionMap = new Map<string, { orders: number; amount: number; pendingOrders: number; cancelledOrders: number }>();
    orders.forEach((order: Order) => {
      const date = new Date(order.date).toISOString().split('T')[0];
      if (!evolutionMap.has(date)) {
        evolutionMap.set(date, { orders: 0, amount: 0, pendingOrders: 0, cancelledOrders: 0 });
      }
      const stats = evolutionMap.get(date)!;
      
      if (order.status === 'paid') {
        stats.orders++;
        stats.amount += order.total;
      } else if (order.status === 'pending') {
        stats.pendingOrders++;
      } else if (order.status === 'cancelled') {
        stats.cancelledOrders++;
      }
    });

    const evolution = Array.from(evolutionMap.entries()).map(([date, stats]) => ({
      date,
      orders: stats.orders,
      amount: stats.amount,
      averageAmount: stats.orders > 0 ? stats.amount / stats.orders : 0,
      pendingOrders: stats.pendingOrders,
      cancelledOrders: stats.cancelledOrders
    }));

    // Évolution des ventes par heure et par jour
    const hourlyMap: OrderStats['hourlyEvolution'] = {};
    
    orders.forEach((order: Order) => {
      if (order.status === 'paid') {
        const orderDate = new Date(order.date);
        const dateKey = orderDate.toISOString().split('T')[0];
        const hour = orderDate.getHours();
        const hourKey = `${hour}h`;
        
        if (!hourlyMap[dateKey]) {
          hourlyMap[dateKey] = [];
        }
        
        let hourData = hourlyMap[dateKey].find(h => h.hour === hourKey);
        
        if (!hourData) {
          hourData = { hour: hourKey, orders: 0, amount: 0, averageAmount: 0 };
          hourlyMap[dateKey].push(hourData);
        }
        
        hourData.orders++;
        hourData.amount += order.total;
        hourData.averageAmount = hourData.amount / hourData.orders;
      }
    });
    
    // Trier les données horaires pour chaque jour
    Object.keys(hourlyMap).forEach(date => {
      hourlyMap[date].sort((a, b) => {
        const hourA = parseInt(a.hour);
        const hourB = parseInt(b.hour);
        return hourA - hourB;
      });
    });

    return {
      totalOrders,
      totalAmount,
      averageAmount,
      conversionRate,
      pendingOrders,
      paidOrders,
      cancelledOrders,
      pendingAmount,
      byClass,
      evolution,
      hourlyEvolution: hourlyMap
    };
  }

  async getProductStats(): Promise<ProductStats> {
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('*');

    if (productsError) {
      throw new Error('Erreur lors de la récupération des produits');
    }

    const { data: orders, error: ordersError } = await supabase
      .from('orders')
      .select('*')
      .order('date', { ascending: true });

    if (ordersError) {
      throw new Error('Erreur lors de la récupération des commandes');
    }

    const totalProducts = products.length;
    const availableProducts = products.filter((product: Product) => product.is_available).length;
    const outOfStockProducts = products.filter((product: Product) => product.stock === 0).length;

    // Initialisation des compteurs
    const articleCounts = products.map((product: Product) => {
      const isPack = product.name.includes('Pack') && 
        !product.name.includes('Forfait') && 
        !product.name.includes('Minimum Syndical');
      const isMinimumSyndical = product.name.includes('Minimum Syndical');
      
      return {
        id: product.id,
        name: product.name,
        paidCount: 0,
        pendingCount: 0,
        stock: product.stock || 0,
        stockPercentage: 0,
        availablePacks: (isPack || isMinimumSyndical) ? 0 : undefined
      };
    });

    // Initialisation des compteurs de tailles
    const sizeCounts: ProductStats['sizeCounts'] = [
      { size: 'S', paidCount: 0, pendingCount: 0 },
      { size: 'M', paidCount: 0, pendingCount: 0 },
      { size: 'L', paidCount: 0, pendingCount: 0 },
      { size: 'XL', paidCount: 0, pendingCount: 0 },
      { size: 'XXL', paidCount: 0, pendingCount: 0 }
    ];

    // D'abord, on compte tous les articles et packs
    orders.forEach((order: any) => {
      // Vérifier si un T-shirt est commandé dans cette commande
      const hasTshirt = order.items.some((item: any) => 
        item.id === 'd85fae00-665e-45ca-927e-213bd67bd61e' || 
        order.customer_info.tshirtSize
      );
      
      if (hasTshirt && order.customer_info && order.customer_info.tshirtSize) {
        const tshirtSize = order.customer_info.tshirtSize;
        const sizeStat = sizeCounts.find(s => s.size === tshirtSize);
        if (sizeStat) {
          if (order.status === 'paid') {
            sizeStat.paidCount++;
          } else if (order.status === 'pending') {
            sizeStat.pendingCount++;
          }
        }
      }

      order.items.forEach((item: any) => {
        const article = articleCounts.find(a => a.id === item.id);
        if (article) {
          if (order.status === 'paid') {
            article.paidCount += item.quantity;
          } else if (order.status === 'pending') {
            article.pendingCount += item.quantity;
          }
        }
      });
    });

    // Ensuite, on ajoute les articles contenus dans les packs
    const packConfigs = {
      'f0118d3c-e8ad-48bc-af35-44b256038821': { // Pack Destruction du JDP
        items: [
          { id: '95833a82-126a-4474-a1aa-67a252f9a1c5', quantity: 1 }, // Forfait Boisson Complet
          { id: 'd85fae00-665e-45ca-927e-213bd67bd61e', quantity: 1 }, // T-Shirt
          { id: '96dc68c5-fe2c-4172-85ea-21c73dfa8f52', quantity: 1 }, // Ecocup
          { id: 'bc994d34-5039-409a-ac82-0bca8a3a1a3b', quantity: 10 }  // Stickers
        ]
      },
      '1c1233ad-2300-4d9b-832b-80fc6251d0d9': { // Pack Kiffeur
        items: [
          { id: 'd85fae00-665e-45ca-927e-213bd67bd61e', quantity: 1 }, // T-Shirt
          { id: 'c2978ada-d2a3-419c-bb13-142520ecec03', quantity: 1 }, // Tote Bag
          { id: '96dc68c5-fe2c-4172-85ea-21c73dfa8f52', quantity: 1 }, // Ecocup
          { id: '95833a82-126a-4474-a1aa-67a252f9a1c5', quantity: 1 }  // Forfait Boisson Complet
        ]
      },
      '984596a0-5029-4e3a-8b7d-8aac57b3d20e': { // Pack Légende
        items: [
          { id: '164b7f1c-c82f-4342-8e48-7950e9b778cf', quantity: 3 }, // Affiche Nuit
          { id: '9a2ad02e-3f8f-4897-a64a-974cc165797c', quantity: 3 }, // Affiche Jour
          { id: 'c2978ada-d2a3-419c-bb13-142520ecec03', quantity: 1 }, // Tote Bag
          { id: '96dc68c5-fe2c-4172-85ea-21c73dfa8f52', quantity: 3 }, // Ecocup
          { id: 'd85fae00-665e-45ca-927e-213bd67bd61e', quantity: 1 }, // T-Shirt
          { id: '95833a82-126a-4474-a1aa-67a252f9a1c5', quantity: 1 }, // Forfait Boisson Complet
          { id: 'bc994d34-5039-409a-ac82-0bca8a3a1a3b', quantity: 25 }  // Stickers
        ]
      },
      '951d6e6a-8619-4ba1-8588-0e4e9aeba949': { // Pack Apéro
        items: [
          { id: 'c2978ada-d2a3-419c-bb13-142520ecec03', quantity: 1 }, // Tote Bag
          { id: '95833a82-126a-4474-a1aa-67a252f9a1c5', quantity: 1 }, // Forfait Boisson Complet
          { id: '96dc68c5-fe2c-4172-85ea-21c73dfa8f52', quantity: 1 }  // Ecocup
        ]
      },
      'eda4605f-b3a7-41d1-9fa9-3f4b3970ec5c': { // Pack Merch
        items: [
          { id: '164b7f1c-c82f-4342-8e48-7950e9b778cf', quantity: 1 }, // Affiche Nuit
          { id: '9a2ad02e-3f8f-4897-a64a-974cc165797c', quantity: 1 }, // Affiche Jour
          { id: 'bc994d34-5039-409a-ac82-0bca8a3a1a3b', quantity: 5 }, // Stickers
          { id: '96dc68c5-fe2c-4172-85ea-21c73dfa8f52', quantity: 1 }, // Ecocup
          { id: '8e32934c-5c44-4c89-8fcc-ccf9358b8782', quantity: 6 }  // Sous-Bocks
        ]
      },
      'f6f27bd8-83de-4dc5-8499-c8cf9efcf524': { // Minimum Syndical
        items: [
          { id: '95833a82-126a-4474-a1aa-67a252f9a1c5', quantity: 1 }, // Forfait Boisson Complet
          { id: '96dc68c5-fe2c-4172-85ea-21c73dfa8f52', quantity: 1 }  // Ecocup
        ]
      },
      '0ee1a6b1-a270-45b9-b721-91326d9ae550': { // Pack Stickers
        items: [
          { id: 'bc994d34-5039-409a-ac82-0bca8a3a1a3b', quantity: 5 }  // Stickers
        ]
      },
      'ca7fb4e8-5f8f-447f-82f0-89b3682e708a': { // Pack Sous-bock
        items: [
          { id: '8e32934c-5c44-4c89-8fcc-ccf9358b8782', quantity: 3 }  // Sous-Bocks
        ]
      }
    };

    // Pour chaque pack, on ajoute ses articles
    console.log('=== Début de la répartition des articles des packs ===');
    console.log(`Nombre total d'articles dans articleCounts: ${articleCounts.length}`);
    
    // Log de tous les IDs des articles
    console.log('Tous les IDs des articles:');
    articleCounts.forEach(article => {
      console.log(`- ${article.id}: ${article.name} (payé: ${article.paidCount}, en attente: ${article.pendingCount})`);
    });

    // Log de tous les packs
    console.log('Configuration des packs:');
    Object.entries(packConfigs).forEach(([packId, config]) => {
      console.log(`Pack ID: ${packId}`);
      console.log(`  Articles contenus: ${config.items.map(item => item.id).join(', ')}`);
    });

    Object.entries(packConfigs).forEach(([packId, config]) => {
      console.log(`\nTraitement du pack: ${packId}`);
      const pack = articleCounts.find(a => a.id === packId);
      if (pack) {
        console.log(`  Pack trouvé: ${pack.name} (${pack.id})`);
        console.log(`  Quantités: payé=${pack.paidCount}, en attente=${pack.pendingCount}`);
        // On ajoute les articles pour les commandes payées et en attente
        config.items.forEach(item => {
          console.log(`  Recherche de l'article: ${item.id} (quantité: ${item.quantity})`);
          const article = articleCounts.find(a => a.id === item.id);
          if (article) {
            console.log(`    Article trouvé: ${article.name}`);
            console.log(`    Avant: payé=${article.paidCount}, en attente=${article.pendingCount}`);
            article.paidCount += pack.paidCount * item.quantity;
            article.pendingCount += pack.pendingCount * item.quantity;
            console.log(`    Après: payé=${article.paidCount}, en attente=${article.pendingCount}`);
          } else {
            console.log(`    Article non trouvé: ${item.id}`);
          }
        });
      } else {
        console.log(`  Pack non trouvé: ${packId}`);
      }
    });
    console.log('=== Fin de la répartition des articles des packs ===');

    // Calcul du pourcentage de stock vendu pour chaque article
    articleCounts.forEach(article => {
      const totalSold = article.paidCount + article.pendingCount;
      if (article.stock > 0) {
        article.stockPercentage = Math.min(100, (totalSold / article.stock) * 100);
      } else {
        article.stockPercentage = 0;
      }
    });

    // Calcul des produits les plus vendus
    const productSales = new Map<string, { 
      totalSold: number; 
      totalRevenue: number;
      paidSold: number;
      pendingSold: number;
    }>();
    
    orders.forEach((order: Order) => {
      order.items.forEach((item) => {
        const current = productSales.get(item.id) || { 
          totalSold: 0, 
          totalRevenue: 0,
          paidSold: 0,
          pendingSold: 0
        };
        productSales.set(item.id, {
          totalSold: current.totalSold + item.quantity,
          totalRevenue: current.totalRevenue + (item.price * item.quantity),
          paidSold: order.status === 'paid' ? current.paidSold + item.quantity : current.paidSold,
          pendingSold: order.status === 'pending' ? current.pendingSold + item.quantity : current.pendingSold
        });
      });
    });

    const topProducts = Array.from(productSales.entries())
      .map(([id, stats]) => {
        const product = products.find((p: Product) => p.id === id);
        return {
          id,
          name: product?.name || 'Produit inconnu',
          totalSold: stats.totalSold,
          totalRevenue: stats.totalRevenue,
          paidSold: stats.paidSold,
          pendingSold: stats.pendingSold
        };
      })
      .sort((a, b) => b.totalSold - a.totalSold)
      .slice(0, 5);

    // Calcul du nombre de packs disponibles pour chaque pack
    Object.entries(packConfigs).forEach(([packId, config]) => {
      const pack = articleCounts.find(a => a.id === packId);
      if (pack) {
        // Pour chaque article dans le pack, on calcule combien de packs on peut faire
        const availablePacks = config.items.map(item => {
          const article = articleCounts.find(a => a.id === item.id);
          if (!article) return 0;
          // On calcule le stock réel disponible en soustrayant les ventes et commandes en attente
          const availableStock = article.stock - (article.paidCount + article.pendingCount);
          // On divise le stock disponible par la quantité nécessaire pour un pack
          return Math.floor(availableStock / item.quantity);
        });
        // Le nombre de packs disponibles est le minimum des packs possibles pour chaque article
        pack.availablePacks = Math.max(0, Math.min(...availablePacks));
      }
    });

    // Calcul spécifique pour le Minimum Syndical
    const minimumSyndical = articleCounts.find(a => a.name.includes('Minimum Syndical'));
    if (minimumSyndical) {
      const config = packConfigs['f6f27bd8-83de-4dc5-8499-c8cf9efcf524']; // ID du Minimum Syndical
      const availablePacks = config.items.map(item => {
        const article = articleCounts.find(a => a.id === item.id);
        if (!article) return 0;
        const availableStock = article.stock - (article.paidCount + article.pendingCount);
        return Math.floor(availableStock / item.quantity);
      });
      minimumSyndical.availablePacks = Math.max(0, Math.min(...availablePacks));
    }

    return {
      totalProducts,
      availableProducts,
      outOfStockProducts,
      topProducts,
      articleCounts,
      sizeCounts
    };
  }

  async updateProductStock(productId: string, stock: number): Promise<void> {
    const { error } = await supabase
      .from('products')
      .update({ stock })
      .eq('id', productId);

    if (error) {
      throw new Error(`Erreur lors de la mise à jour du stock: ${error.message}`);
    }
  }
}

export const statsService = StatsService.getInstance(); 